import { useState } from 'react';
import { type UpsertResourceRequest, type TopicResource } from '@/lib/api/trainer';

const RESOURCE_TYPES: { value: string; label: string; icon: string; placeholder: string }[] = [
  { value: 'link', label: 'Link', icon: '🔗', placeholder: 'https://...' },
  { value: 'pdf', label: 'PDF', icon: '📄', placeholder: 'https://drive.google.com/...' },
  { value: 'slides', label: 'Slides', icon: '🖼️', placeholder: 'https://slides.google.com/...' },
  { value: 'code_repo', label: 'Code Repo', icon: '💻', placeholder: 'https://github.com/...' },
  { value: 'recording', label: 'Recording', icon: '📹', placeholder: 'https://drive.google.com/...' },
  { value: 'notes', label: 'Notes', icon: '📝', placeholder: 'https://notion.so/...' },
];

interface Props {
  initial?: TopicResource;
  onSave: (req: UpsertResourceRequest) => Promise<void>;
  onCancel: () => void;
}

const inputClass = 'w-full rounded-md border border-[#ececec] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const labelClass = 'mb-1.5 block text-xs font-medium text-[#6b7280]';

export function ResourceForm({ initial, onSave, onCancel }: Props) {
  const [type, setType] = useState(initial?.type ?? 'link');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [url, setUrl] = useState(initial?.url ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [isRequired, setIsRequired] = useState(initial?.is_required ?? false);
  const [busy, setBusy] = useState(false);

  const selected = RESOURCE_TYPES.find((t) => t.value === type) ?? RESOURCE_TYPES[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave({
        title,
        type,
        url,
        description: description.trim() || undefined,
        is_required: isRequired || undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Resource type */}
      <div>
        <label className={labelClass}>Resource type *</label>
        <div className="grid grid-cols-3 gap-2">
          {RESOURCE_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setType(t.value)}
              className={`flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm transition-colors ${
                type === t.value
                  ? 'border-orange-400 bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400'
                  : 'border-[#ececec] text-[#6b7280] hover:bg-[#fafafa]'
              }`}
            >
              <span>{t.icon}</span>
              <span className="font-medium">{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* URL */}
      <div>
        <label className={labelClass}>URL *</label>
        <input
          required
          type="url"
          autoFocus
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={selected.placeholder}
          className={inputClass}
        />
      </div>

      {/* Title */}
      <div>
        <label className={labelClass}>Title *</label>
        <input
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. React Hooks Slides"
          className={inputClass}
        />
      </div>

      {/* Description */}
      <div>
        <label className={labelClass}>Notes for students</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="e.g. Read pages 10–35. Focus on the useEffect section."
          className={inputClass}
          style={{ resize: 'vertical' }}
        />
      </div>

      {/* Required toggle */}
      <label className="flex cursor-pointer items-center gap-2.5 text-sm">
        <input
          type="checkbox"
          checked={isRequired}
          onChange={(e) => setIsRequired(e.target.checked)}
          className="h-4 w-4 rounded border-[#ececec] accent-orange-500"
        />
        <span className="font-medium">Required</span>
        <span className="text-[#6b7280] text-xs">(shown as mandatory on learner dashboard)</span>
      </label>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="rounded-md border border-[#ececec] px-4 py-2 text-sm hover:bg-[#fafafa] transition-colors">
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy || !title || !url}
          className="rounded-md bg-[#ff5a1f] px-4 py-2 text-sm font-medium text-white disabled:opacity-50 transition-colors"
        >
          {busy ? 'Saving…' : initial ? 'Update resource' : 'Add resource'}
        </button>
      </div>
    </form>
  );
}
